import { Component, Inject, NgZone, OnDestroy, OnInit } from '@angular/core';
import { Observable } from 'rxjs';
import { SignalrService } from './services/signalR/signalr.service';
import { Select, Store } from '@ngxs/store';
import { ActionsSubject } from '@ngrx/store';
import { Router } from '@angular/router';
import { InitFilters } from './state-management/actions/filters.actions';
import { OrganizationsState } from './state-management/states/organizations.states';
import { MAT_SNACK_BAR_DATA, MatSnackBar } from '@angular/material/snack-bar';
import { APIService } from './services/api.service';
import { SnackbarService } from './services/snackbar.service';
import { SnackbarComponent } from './shared/components/snackbar/snackbar.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ofType } from "@ngrx/effects";
import { signOutUser } from "app/reducers/context.actions";
import { environment } from 'environments/environment';

@UntilDestroy()
@Component({
  selector: 'app-root',
  template: `
    <app-preloader> <router-outlet></router-outlet></app-preloader>
   `
})
export class AppComponent implements OnInit, OnDestroy {
  @Select(OrganizationsState.getSelectedOrganizationId) getSelectedOrganizationId$: Observable<string>;

  constructor(
    private router: Router,
    private signalrService: SignalrService,
    private store: Store,
    private snackBar: MatSnackBar,
    private api: APIService,
    private snackbarService: SnackbarService,
    protected readonly storeActions: ActionsSubject,
    private _ngZone: NgZone
  ) {
  }

  async ngOnInit(): Promise<void> {
    console.info(`V: ${environment.lastHashCommit}`);
    await this.signalrService.initAsync();

    this.storeActions.pipe(
      ofType(signOutUser)
    ).subscribe(value => {
      // Если мы уже на форме логина — ничего не делаем. Иначе пачка 401 от
      // ещё висящих запросов пересоздаёт LoginComponent и стирает введённые
      // логин с паролем.
      if (this.router.url.startsWith('/auth')) {
        return;
      }

      // Точка возврата уходит явным query-параметром. Раньше здесь стоял флаг
      // state.canComeBack, по которому форма логина делала location.back():
      // стек истории вкладки приложению неизвестен, поэтому back() уводил из
      // приложения (IC-2673).
      const returnUrl = this.resolveReturnUrl();
      this._ngZone.run(() => {
        this.router.navigate(['auth'], returnUrl ? {queryParams: {returnUrl}} : {});
      });
    })

    this.getSelectedOrganizationId$
      .pipe(
        untilDestroyed(this)
      )
      .subscribe(selectedOrganizationId => {
        if (selectedOrganizationId) {
          this.store.dispatch(new InitFilters());
        }
      });

    this.api.rpcError$
      .pipe(
        untilDestroyed(this)
      )
      .subscribe(data => {
        this.snackBar.openFromComponent(ErrorSnackBarComponent, {
          duration: 5000,
          horizontalPosition: 'end',
          verticalPosition: 'top',
          data,
          panelClass: 'error-contaner'
        });
      });

    this.snackbarService.message$
      .pipe(
        untilDestroyed(this)
      )
      .subscribe((message) => {
        this.snackBar.openFromComponent(SnackbarComponent, {
          duration: 5000,
          horizontalPosition: 'end',
          verticalPosition: 'top',
          data: message,
          panelClass: 'snackbar-' + message.type
        });
      });
  }

  /**
   * Адрес, на который вернуть пользователя после логина. router.url на этапе
   * бутстрапа может быть ещё '/', поэтому подстраховываемся адресом документа.
   * На саму форму логина не возвращаем.
   */
  private resolveReturnUrl(): string | null {
    const routerUrl = this.router.url;
    const url = routerUrl && routerUrl !== '/'
      ? routerUrl
      : window.location.pathname + window.location.search;

    return url.startsWith('/auth') ? null : url;
  }

  async ngOnDestroy(): Promise<void> {
    await this.signalrService.disconnectAsync();
  }
}

@Component({
  selector: 'app-snack-bar-error',
  template: `
    <div class="code">Error code: {{data.code}}</div>
    <div class="message"> Message: {{data.message}}</div>
  `,
  styles: [`
    .code, .message {
      color: maroon;
      min-width: 300px;
    }

    .code {
      font-size: 1.2em;
    }
  `],
})
export class ErrorSnackBarComponent {
  constructor(@Inject(MAT_SNACK_BAR_DATA) public data: any) {
  }
}
